import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { SignataireForm } from '@lib/components/signature/models/signature.form.model';
import { QueryManagerService } from 'finzzle-angular-utils';
import { lastValueFrom, Subject } from 'rxjs';
import {
  CancelSignaturesGQL,
  CreateSignaturesGQL,
  DocumentSignatureInput,
  Provider,
  RelanceSignaturesGQL,
  SignataireInput,
  SignataireType,
} from '../../public-api';
@Injectable({ providedIn: 'root' })
export class SignatureService {
  private readonly createSignaturesGQL = inject(CreateSignaturesGQL);
  private readonly cancelSignaturesGQL = inject(CancelSignaturesGQL);
  private readonly relanceSignaturesGQL = inject(RelanceSignaturesGQL);
  private readonly queryManagerService = inject(QueryManagerService);
  private readonly environment = inject<any>('environment' as any);
  private refreshDataSubject = new Subject<void>();
  public refreshData$ = this.refreshDataSubject.asObservable();
  //#region Graphql Requests
  public async createSignatures(input: DocumentSignatureInput[]): Promise<string[] | undefined> {
    const result = await this.queryManagerService.handleMutationErrorsAsync(
      this.createSignaturesGQL.mutate({
        errorPolicy: 'all',
        variables: { input },
      })
    );
    const createdSignatures = result?.data?.createSignatures;
    if (!createdSignatures?.length) {
      throw result?.error ?? new Error('Unable to create signatures');
    }
    return createdSignatures;
  }
  public async cancelSignatures(documentIds: string[]): Promise<string[] | undefined> {
    const result = await lastValueFrom(
      this.cancelSignaturesGQL.mutate({
        errorPolicy: 'all',
        variables: { documentIds },
      })
    );
    const cancelledSignatures = result?.data?.cancelSignatures;
    if (!cancelledSignatures?.length) {
      throw result?.error ?? new Error('Unable to cancel signatures');
    }
    return cancelledSignatures;
  }
  public async relanceSignatures(documentIds: string[]): Promise<boolean | undefined> {
    const result = await lastValueFrom(
      this.relanceSignaturesGQL.mutate({
        errorPolicy: 'all',
        variables: { documentIds },
      })
    );
    const relanceResult = result?.data?.relanceSignatures;
    if (!relanceResult) {
      throw result?.error ?? new Error('Unable to relance signatures');
    }
    return relanceResult;
  }
  //#endregion
  //#region Utils
  public buildDocumentSignatureInput(documentIds: string[], signataireInputs: SignataireInput[]): DocumentSignatureInput {
    return {
      documentIds: documentIds,
      providerSettings: { providerId: Provider.UniversignU1, providerContextId: '' },
      signataire: signataireInputs,
    };
  }
  public buildSignatairesInput(signataireForms: FormGroup<SignataireForm>[]): SignataireInput[] {
    const signataires: SignataireInput[] = [];
    signataireForms.forEach(s => {
      const signataire = s.value;
      const signataireInput: SignataireInput = {
        email: signataire.email?.adresse as string,
        firstName: signataire.firstName as string,
        lastName: signataire.lastName as string,
        phone: signataire.telephone?.numero as string,
        internalId: signataire.id as number,
        type: signataire.signataireType as SignataireType,
      };
      signataires.push(this.applyTestEnvironmentContact(signataireInput));
    });
    return signataires;
  }

  public applyTestEnvironmentContact(signataire: SignataireInput): SignataireInput {
    if (['dev', 'int'].includes(this.environment.env) && signataire.type === SignataireType.Consultant) {
      return {
        ...signataire,
        email: this.environment.signatureTestMail,
        phone: this.environment.signatureTestPhone,
      };
    }
    return signataire;
  }
  public triggerRefreshData(): void {
    this.refreshDataSubject.next();
  }
}
